import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { X, Minus, Maximize2 } from 'lucide-react';

const WindowContainer = styled(motion.div)`
  position: absolute;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
  z-index: ${props => props.customZIndex || (props.isActive ? 100 : 10)};
  cursor: ${props => props.isDragging ? 'grabbing' : 'default'};
`;

const WindowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.8);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  cursor: ${props => props.isDragging ? 'grabbing' : 'grab'};
  user-select: none;
`;

const WindowControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ControlButton = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 8px;
  color: transparent;
  
  &:hover {
    color: rgba(0, 0, 0, 0.5);
  }
  
  &.close {
    background: #FF5F57;
    
    &:hover {
      background: #FF4444;
    }
  }
  
  &.minimize {
    background: #FFBD2E;
    
    &:hover {
      background: #FFB020;
    }
  }
  
  &.maximize {
    background: #28CA42;
    
    &:hover {
      background: #24C040;
    }
  }
`;

const WindowTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #333;
  flex: 1;
  text-align: center;
  margin: 0 60px;
`;

const WindowContent = styled.div`
  height: calc(100% - 45px);
  overflow: hidden;
`;

const ResizeHandle = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 20px;
  height: 20px;
  cursor: nw-resize;
  background: linear-gradient(-45deg, transparent 30%, rgba(0, 0, 0, 0.1) 30%, rgba(0, 0, 0, 0.1) 70%, transparent 70%);
  
  &:hover {
    background: linear-gradient(-45deg, transparent 30%, rgba(0, 0, 0, 0.2) 30%, rgba(0, 0, 0, 0.2) 70%, transparent 70%);
  }
`;

const Window = ({ 
  children, 
  title, 
  position, 
  size, 
  isActive, 
  onClose, 
  onMinimize, 
  onActivate, 
  onPositionChange, 
  onSizeChange,
  zIndex,
  ...props 
}) => {
  const [isDragging, setIsDragging] = useState(false);
  
  const handleDragStart = () => {
    setIsDragging(true);
    onActivate();
  };
  
  const handleDragEnd = (event, info) => {
    setIsDragging(false);
    if (onPositionChange) {
      onPositionChange({ 
        x: position.x + info.offset.x, 
        y: position.y + info.offset.y 
      });
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    if (onClose) {
      onClose();
    }
  };

  const handleMinimize = (e) => {
    e.stopPropagation();
    if (onMinimize) {
      onMinimize();
    }
  };

  return (
    <WindowContainer
      style={{ 
        left: position.x, 
        top: position.y, 
        width: size.width, 
        height: size.height
      }}
      customZIndex={zIndex}
      drag
      dragMomentum={false}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      isDragging={isDragging}
      isActive={isActive}
      onClick={onActivate}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.2 }}
      {...props}
    >
      <WindowHeader 
        isDragging={isDragging}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <WindowControls>
          <ControlButton className="close" onClick={handleClose}>
            <X size={8} />
          </ControlButton>
          <ControlButton className="minimize" onClick={handleMinimize}>
            <Minus size={8} />
          </ControlButton>
          <ControlButton className="maximize">
            <Maximize2 size={8} />
          </ControlButton>
        </WindowControls>
        <WindowTitle>{title}</WindowTitle>
      </WindowHeader>
      
      <WindowContent>
        {children}
      </WindowContent>
      
      <ResizeHandle />
    </WindowContainer>
  );
};

export default Window;
